import { ADD_ON_IDS, FE_ADD_ON_ID } from '@ctct/domain-layer';
import { buyNowQueryParamsSessionStorage, urlParams } from '@ctct/infrastructure-layer';

import type { Listeners } from '../helpers';
import { controllerHelpers } from '../helpers';
import {
  type BillingInfoFormManager,
  type EmailPlansManager,
  type OrderSummaryManager,
  type NonProfitManager,
  type CurrencyManager,
  type PrepaySelectManager,
  type ContactsTierSelectManager,
  type PromoCodesManager,
  emailPlansEventBus,
  type AddOnsManager,
  type EmailPlanChangedPayload,
  addOnsEventBus,
} from '../managers';
import { NON_PROFIT_PAYMENT_PLAN_ID } from '../managers/NonProfitManager/services/NonProfitService';

export class OrderSummarySetupController {
  public listeners: Listeners;

  constructor(
    private orderSummaryManager: OrderSummaryManager,
    private billingInfoFormManager: BillingInfoFormManager,
    private nonProfitManager: NonProfitManager,
    private currencyManager: CurrencyManager,
    private prepaySelectManager: PrepaySelectManager,
    private contactsTierSelectManager: ContactsTierSelectManager,
    private emailPlansManager: EmailPlansManager,
    private promoCodesManager: PromoCodesManager,
    private addOnsManager?: AddOnsManager
  ) {
    this.listeners = controllerHelpers.createListeners([
      emailPlansEventBus.bindTo('emailPlanChanged', this.onEmailPlanChanged),
      addOnsEventBus.bindTo('addOnAdded', this.onAddOnAdded),
      addOnsEventBus.bindTo('addOnRemoved', this.onAddOnRemoved),
    ]);
  }

  private onAddOnAdded = async (addOnId: string) => {
    if (addOnId === FE_ADD_ON_ID.XERO) {
      const api = await this.orderSummaryManager.getApiWhenReady();

      api.addXeroAddon?.();
    }
  };

  private onAddOnRemoved = async (addOnId: string) => {
    if (addOnId === FE_ADD_ON_ID.XERO) {
      const api = await this.orderSummaryManager.getApiWhenReady();

      api.removeXeroAddon?.();
    }
  };

  private onEmailPlanChanged = async ({ planId }: EmailPlanChangedPayload) => {
    const api = await this.orderSummaryManager.getApiWhenReady();
    const billingInfoApi = await this.billingInfoFormManager.getApiWhenReady();
    const nonProfitApi = await this.nonProfitManager.getApiWhenReady();
    const currencyApi = await this.currencyManager.getApiWhenReady();
    const prepaySelectApi = await this.prepaySelectManager.getApiWhenReady();
    const contactsTierSelectApi = await this.contactsTierSelectManager.getApiWhenReady();
    const emailPlansApi = await this.emailPlansManager.getApiWhenReady();
    const newPromotionCodes = this.orderSummaryManager.getNewPromotionCodeNamesFromPayload();
    const addonsApi = await this.addOnsManager?.getApiWhenReady();

    emailPlansApi.setSelectedPlanId(planId);

    const currentRating = api.getRatingPayload();

    const newAddOnServices = addonsApi?.getSelectedAddons().map((addon) => addon.id) || [];

    if (newAddOnServices.includes(FE_ADD_ON_ID.XERO)) {
      api.addXeroAddon?.();
    }

    const smsAmount = addonsApi?.getSelectedAddons().find((addon) => addon.id === ADD_ON_IDS.SMS)?.option || undefined;

    if (!currentRating) {
      // Read URL params directly as fallback for race condition: managers may delete params before updating stores
      const currencyFromUrl = urlParams.getCurrency() ?? buyNowQueryParamsSessionStorage.getParam('currency');
      const paymentPeriodFromUrl = urlParams.getPaymentPeriod() ?? buyNowQueryParamsSessionStorage.getPaymentPeriod();
      const nonprofitFromUrl = urlParams.getNonprofit() ?? buyNowQueryParamsSessionStorage.getParam('nonprofit');

      let currencyCode = currencyApi.getCurrency();
      let paymentPeriod = parseInt(prepaySelectApi.getPrepay(), 10);
      let paymentPlanId = nonProfitApi.getPaymentPlanGroupId();
      const contactTier = contactsTierSelectApi.getContactTier();

      if (currencyFromUrl && currencyFromUrl.toUpperCase() !== currencyCode?.toUpperCase()) {
        currencyCode = currencyFromUrl.toUpperCase();
      }

      if (paymentPeriodFromUrl && parseInt(paymentPeriodFromUrl, 10) !== paymentPeriod) {
        paymentPeriod = parseInt(paymentPeriodFromUrl, 10);
      }

      if (nonprofitFromUrl === 'true') {
        paymentPlanId = NON_PROFIT_PAYMENT_PLAN_ID;
      }

      api.initRatingPayload(
        {
          newPlanId: planId,
          currencyCode,
          customer: billingInfoApi.getCustomer(currencyCode),
          paymentPeriod,
          paymentPlanId,
          planGroupId: emailPlansApi.getEmailPlanGroupId(),
          currentPlanId: emailPlansApi.getCurrentPlanId(),
          newPromotionCodes,
          newAddOnServices: newAddOnServices.filter((id) => id !== FE_ADD_ON_ID.XERO),
          includeRemainingBalance: false,
        },
        contactTier,
        smsAmount
      );
    } else {
      // A payload restored from session storage may have a stale planGroupId (e.g., 379 for a
      // Micro Email paid user who should use group 109). Always sync planGroupId to the
      // authoritative value so the rating call uses the correct plan group.
      api.updatePlanGroupId(emailPlansApi.getEmailPlanGroupId());
    }
  };
}
